import React, { useState, useEffect, useCallback } from 'react';
import { getCEActivity } from '../api';

/* ──────────────────────────────────────────────────────────────────
 * CE Activity Page — role-scoped view of CE Agent's automation job
 * history.
 *
 * Scope (computed server-side; client just renders what comes back):
 *   PM (rank 1)            → own jobs only
 *   CM / CD (rank 2-3)     → self + everyone in reports_to subtree
 *   VP / Admin (rank 4-5)  → every job
 *
 * Columns: Job · Type · Status · Project · Requested by · Started ·
 * Duration · Message preview.
 *
 * Click a row to expand a drawer with the full message/error and any
 * replay-lineage links (read-only — historical context only). Replay
 * functionality is intentionally NOT exposed to end users; if a job
 * needs to be re-run, it's an operator action handled from the CE
 * Agent's own /dashboard/history page, not from Summit.
 *
 * 2026-05-28: warm-cream + sage token redesign to match the rest of
 * the chrome (Dashboard / Schedule / SOV / Billing / Rollup /
 * Performance). Drops `useTheme` + hardcoded hex; status badges use
 * --success / --warning / --danger / --accent semantics with 22%-mix
 * soft rings.
 * ────────────────────────────────────────────────────────────────── */

/* ─────────────────────── Status tokens ─────────────────────── */

const STATUS_META = {
  queued:         { label: 'Queued',    tone: 'neutral' },
  running:        { label: 'Running',   tone: 'accent'  },
  parked_waiting: { label: 'Parked',    tone: 'warning' },
  completed:      { label: 'Completed', tone: 'success' },
  partial:        { label: 'Partial',   tone: 'warning' },
  failed:         { label: 'Failed',    tone: 'danger'  },
};

const SCOPE_LABEL = {
  self: 'Showing your jobs',
  team: 'Showing your team',
  all:  'Showing all jobs',
};

/* ─────────────────────── Helpers ─────────────────────── */

// '14:32 May 27' style — compact + readable, no year for current year.
const fmtTimestamp = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    const sameYear = dt.getFullYear() === new Date().getFullYear();
    return dt.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    }) + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
};

// Compact duration: 1.3s / 47s / 2m 14s / 1h 5m.
const fmtDuration = (s) => {
  if (s == null) return '–';
  const sec = Number(s);
  if (!Number.isFinite(sec)) return '–';
  if (sec < 10) return sec.toFixed(1) + 's';
  if (sec < 60) return Math.round(sec) + 's';
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const ss = Math.round(sec % 60);
    return `${m}m ${ss}s`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
};

// 'JobType.CREATE_SOV' / 'create_sov' / 'CREATE_SOV' → 'CREATE_SOV'
const cleanJobType = (s) => String(s || '').replace(/^JobType\./, '').toUpperCase();
// Same idea for status: 'JobStatus.COMPLETED' → 'completed'
const cleanStatus = (s) => String(s || '').replace(/^JobStatus\./, '').toLowerCase();

/* ─────────────────────── Inline icons ─────────────────────── */

const RefreshIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const ChevronDown = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CheckIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const ClockIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const GearIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const PauseIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);
const HalfCircleIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 A10 10 0 0 1 12 22 Z" />
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const Spinner = ({ size = 12, color = 'currentColor' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const STATUS_ICONS = {
  queued:         ClockIcon,
  running:        GearIcon,
  parked_waiting: PauseIcon,
  completed:      CheckIcon,
  partial:        HalfCircleIcon,
  failed:         XIcon,
};

/* ─────────────────────── Status badge ─────────────────────── */

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const Icon = STATUS_ICONS[status] || ClockIcon;
  const palette = {
    success: { fg: 'var(--success)',    bg: 'var(--success-soft)' },
    warning: { fg: 'var(--warning)',    bg: 'var(--warning-soft)' },
    danger:  { fg: 'var(--danger)',     bg: 'var(--danger-soft)'  },
    accent:  { fg: 'var(--accent-text)', bg: 'var(--accent-soft)' },
    neutral: { fg: 'var(--text-3)',     bg: 'var(--bg-surface-2)' },
  }[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px 2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500,
      color: palette.fg, background: palette.bg,
      border: `1px solid color-mix(in oklch, ${palette.fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      <span aria-hidden style={{ display: 'inline-flex' }}><Icon /></span>
      {meta.label}
    </span>
  );
};

/* ─────────────────────── Signature badge ─────────────────────── */

// dropbox_sign_status (set by CE-Agent on send, updated by Summit's
// Dropbox Sign webhook receiver) → label + tone. Values match the enum
// written by sync_db.set_dropbox_sign_envelope + routes/dropboxSignWebhook.
const SIGNATURE_META = {
  sent:             { label: 'Sent',     tone: 'neutral' },
  viewed:           { label: 'Viewed',   tone: 'accent'  },
  partially_signed: { label: 'Signing',  tone: 'warning' },
  completed:        { label: 'Signed',   tone: 'success' },
  declined:         { label: 'Declined', tone: 'danger'  },
  canceled:         { label: 'Canceled', tone: 'neutral' },
  expired:          { label: 'Expired',  tone: 'danger'  },
};

const TONE_PALETTE = {
  success: { fg: 'var(--success)',     bg: 'var(--success-soft)'  },
  warning: { fg: 'var(--warning)',     bg: 'var(--warning-soft)'  },
  danger:  { fg: 'var(--danger)',      bg: 'var(--danger-soft)'   },
  accent:  { fg: 'var(--accent-text)', bg: 'var(--accent-soft)'   },
  neutral: { fg: 'var(--text-3)',      bg: 'var(--bg-surface-2)'  },
};

// Renders the AFP signature-envelope state. A job with no envelope
// (dropbox_sign_status null) renders an em-dash — most jobs aren't
// CREATE_SOV and never fire an envelope. test_mode envelopes get a
// small TEST chip so a real signed AFP is never confused with a test.
const SignatureBadge = ({ status, testMode }) => {
  const s = String(status || '').toLowerCase();
  const meta = SIGNATURE_META[s];
  if (!meta) return <span style={{ color: 'var(--text-4)', fontSize: 13.5 }}>–</span>;
  const palette = TONE_PALETTE[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500,
      color: palette.fg, background: palette.bg,
      border: `1px solid color-mix(in oklch, ${palette.fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      {meta.label}
      {testMode ? (
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          padding: '1px 4px', borderRadius: 4,
          color: 'var(--text-3)', background: 'var(--bg-surface-2)',
          border: '1px solid var(--border-subtle)',
        }}>TEST</span>
      ) : null}
    </span>
  );
};

/* ─────────────────────── Shared input styles ─────────────────────── */

const selectStyle = {
  height: 32, padding: '0 28px 0 10px',
  fontSize: 14, color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  outline: 'none', cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  backgroundImage: 'none',
};
const inputStyle = {
  height: 32, padding: '0 10px',
  fontSize: 14, color: 'var(--text-1)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  outline: 'none', boxSizing: 'border-box',
};
const onFocusRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--accent)';
  e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent-ring)';
};
const onBlurRing = (e) => {
  e.currentTarget.style.borderColor = 'var(--border-default)';
  e.currentTarget.style.boxShadow = 'none';
};

/* ─────────────────────── Filter select with caret ─────────────────────── */

const FilterSelect = ({ value, onChange, children, minWidth = 140 }) => (
  <div style={{ position: 'relative', display: 'inline-block', minWidth }}>
    <select
      value={value}
      onChange={onChange}
      style={{ ...selectStyle, width: '100%' }}
      onFocus={onFocusRing}
      onBlur={onBlurRing}
    >
      {children}
    </select>
    <span style={{
      position: 'absolute',
      right: 10, top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--text-3)',
      pointerEvents: 'none',
      display: 'inline-flex',
    }}>
      <ChevronDown />
    </span>
  </div>
);

/* ─────────────────────── Grid columns ─────────────────────── */

const COL_TEMPLATE = '110px 150px 105px 115px 105px 150px 125px 75px 1fr';

/* ─────────────────────── Main page ─────────────────────── */

/* The CE Agent is a separate process on a VM, so "unreachable" is a routine state
   rather than an exception -- the VM reboots, or the agent was never started. Its own
   message is right for a log and wrong for a banner: "CE Agent request timed out after
   15000ms" tells a user nothing they can act on, and it hides the actual meaning.
   Note what a FULL-LENGTH timeout implies: a reachable-but-closed port refuses
   immediately, so 15s of silence means packets were dropped -- i.e. nothing is
   listening on 8443. "The agent probably is not running" is the honest reading, and it
   is the one that points somebody at the fix. The raw text stays in console.error and
   in the banner's title attribute so it is still one hover away. */
const friendlyCEError = (raw) => {
  const m = String(raw || '');
  if (/timed out after/i.test(m)) return 'Couldn\u2019t reach the CE Agent \u2014 it may not be running on the VM.';
  if (/ECONNREFUSED|refused/i.test(m)) return 'The CE Agent refused the connection \u2014 it may be restarting.';
  if (/\b50[23]\b|Bad Gateway|Service Unavailable/i.test(m)) return 'The CE Agent is unavailable right now \u2014 it may be restarting.';
  if (/\b40[13]\b|signature|hmac/i.test(m)) return 'Summit could not authenticate to the CE Agent \u2014 the shared secret needs a look.';
  return m || 'Failed to fetch CE Activity';
};

const CEActivityPage = ({ onOpenProject }) => {
  const [data, setData] = useState({ jobs: [], total: 0, scope: 'self' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProject, setFilterProject] = useState('');

  // Inject spin keyframe once for the Spinner.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'ce-activity-anim';
    if (document.getElementById(id)) return undefined;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
    return () => {};
  }, []);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(null);
      const params = { limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.job_type = filterType;
      if (filterProject) params.project_id = filterProject;
      const result = await getCEActivity(params);
      setData(result);
    } catch (e) {
      console.error('CE Activity fetch failed:', e);
      setError({ text: friendlyCEError(e.message), raw: e.message || '' });
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [filterStatus, filterType, filterProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div style={{
      padding: '20px 24px 24px',
      display: 'flex', flexDirection: 'column', gap: 16,
      maxWidth: 1600, margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{
            margin: 0, fontSize: 23.5, fontWeight: 600,
            color: 'var(--text-1)', letterSpacing: '-0.02em',
          }}>CE Activity</h1>
          <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 3 }}>
            {SCOPE_LABEL[data.scope] || 'Job history from the ComputerEase automation agent.'}
            {data.total > 0 && (
              <>
                {' · '}
                <span className="mono" style={{ letterSpacing: '-0.02em' }}>
                  {data.total}
                </span>{' '}
                job{data.total === 1 ? '' : 's'} total
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchData({ silent: true })}
          disabled={refreshing}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            height: 32, padding: '0 12px', borderRadius: 8,
            fontSize: 14, fontWeight: 500,
            color: 'var(--text-2)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
            cursor: refreshing ? 'wait' : 'pointer',
            opacity: refreshing ? 0.85 : 1,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { if (!refreshing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
        >
          {refreshing ? <Spinner size={11} /> : <RefreshIcon />}
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <FilterSelect
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          minWidth={150}
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="partial">Partial</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="queued">Queued</option>
          <option value="parked_waiting">Parked</option>
        </FilterSelect>
        <FilterSelect
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          minWidth={170}
        >
          <option value="">All job types</option>
          <option value="CREATE_NEW_JOB">Create New Job</option>
          <option value="CREATE_SOV">Create SOV</option>
          <option value="UPDATE_SOV_PROGRESS">Update SOV Progress</option>
          <option value="PRINT_PAY_APP">Print Pay App</option>
          <option value="RUN_QTOOL_REPORT">Q Tool Report</option>
        </FilterSelect>
        <input
          type="text"
          placeholder="Filter by project ID…"
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          style={{ ...inputStyle, minWidth: 200, flex: '0 0 auto' }}
          onFocus={onFocusRing}
          onBlur={onBlurRing}
        />
      </div>

      {/* Table / loading / empty / error */}
      {loading ? (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          padding: '60px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          color: 'var(--text-3)', fontSize: 14.5,
        }}>
          <Spinner size={18} color="var(--accent)" /> Loading CE Activity…
        </div>
      ) : error ? (
        <div title={error.raw || undefined} style={{
          margin: 0, padding: '12px 14px 12px 16px',
          background: 'var(--danger-soft)',
          borderRadius: 8,
          boxShadow: 'inset 3px 0 0 var(--danger)',
          border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)',
          color: 'var(--danger)', fontSize: 14,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 200 }}>{error.text}</span>
          <button
            type="button"
            onClick={() => fetchData()}
            style={{ flex: '0 0 auto', height: 28, padding: '0 12px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--danger)', background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--danger) 35%, transparent)' }}
          >
            Try again
          </button>
        </div>
      ) : data.jobs.length === 0 ? (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          padding: '48px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 15.5, color: 'var(--text-2)', marginBottom: 4 }}>
            No CE Agent jobs match these filters.
          </div>
          {data.scope === 'self' && (
            <div style={{ fontSize: 13.5, color: 'var(--text-4)' }}>
              You only see your own jobs as a Project Manager.
            </div>
          )}
        </div>
      ) : (
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 'calc(100vh - 280px)', minHeight: 240, overflow: 'auto' }}>
            {/* Sticky header */}
            <div style={{
              display: 'grid', gridTemplateColumns: COL_TEMPLATE, columnGap: 12,
              height: 38, alignItems: 'center',
              padding: '0 16px',
              position: 'sticky', top: 0, zIndex: 3,
              background: 'var(--bg-surface-2)',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              {['Job', 'Type', 'Status', 'Signature', 'Project', 'Requested by',
                'Started', 'Duration', 'Message'].map((label) => (
                <div key={label} style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text-3)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}>{label}</div>
              ))}
            </div>

            {data.jobs.map((j, idx) => {
              const status = cleanStatus(j.status);
              const isOpen = expandedId === j.job_id;
              const isLast = idx === data.jobs.length - 1;
              return (
                <React.Fragment key={j.job_id}>
                  <div
                    onClick={() => setExpandedId(isOpen ? null : j.job_id)}
                    style={{
                      display: 'grid', gridTemplateColumns: COL_TEMPLATE, columnGap: 12,
                      minHeight: 56, alignItems: 'center',
                      padding: '0 16px',
                      borderBottom: (!isLast || isOpen) ? '1px solid var(--border-subtle)' : 'none',
                      background: isOpen ? 'var(--bg-hover)' : 'var(--bg-surface)',
                      cursor: 'pointer',
                      transition: 'background .12s',
                    }}
                    onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                  >
                    <div className="mono" style={{
                      fontSize: 12.5, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.job_id}>
                      {String(j.job_id).slice(0, 12)}…
                    </div>
                    <div style={{
                      fontSize: 14, color: 'var(--text-1)', fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {cleanJobType(j.job_type)}
                    </div>
                    <div><StatusBadge status={status} /></div>
                    <div>
                      <SignatureBadge
                        status={j.dropbox_sign_status}
                        testMode={j.dropbox_sign_test_mode}
                      />
                    </div>
                    <div
                      className="mono"
                      style={{
                        fontSize: 13, color: 'var(--text-3)',
                        letterSpacing: '-0.02em',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        cursor: onOpenProject && j.project_id ? 'pointer' : 'default',
                      }}
                      onClick={(e) => {
                        if (onOpenProject && j.project_id) {
                          e.stopPropagation();
                          onOpenProject(j.project_id);
                        }
                      }}
                      title={j.project_id || ''}
                    >
                      {j.project_id || '–'}
                    </div>
                    <div style={{
                      fontSize: 14, color: 'var(--text-2)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.requested_by}>
                      {j.requested_by || '–'}
                    </div>
                    <div className="mono" style={{
                      fontSize: 13, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                      whiteSpace: 'nowrap',
                    }}>
                      {fmtTimestamp(j.started_at || j.requested_at)}
                    </div>
                    <div className="mono" style={{
                      fontSize: 13, color: 'var(--text-3)',
                      letterSpacing: '-0.02em',
                    }}>
                      {fmtDuration(j.duration_seconds)}
                    </div>
                    <div style={{
                      fontSize: 13.5, color: status === 'failed' ? 'var(--danger)' : 'var(--text-3)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={j.error_message || j.message || ''}>
                      {j.error_message || j.message || '–'}
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{
                      background: 'var(--bg-surface-2)',
                      borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                    }}>
                      <ExpandedDrawer job={j} onOpenProject={onOpenProject} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Expanded drawer ─────────────────────── */

const ExpandedDrawer = ({ job, onOpenProject }) => {
  const status = cleanStatus(job.status);
  const showFullMessage = job.error_message || job.message;
  return (
    <div style={{ padding: '14px 22px 16px', fontSize: 14 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr',
        gap: '6px 16px', marginBottom: 14,
      }}>
        <DrawerKV label="Job ID" mono><span title={job.job_id}>{job.job_id}</span></DrawerKV>
        <DrawerKV label="Type">{cleanJobType(job.job_type)}</DrawerKV>
        <DrawerKV label="Status"><StatusBadge status={status} /></DrawerKV>
        {job.project_id && (
          <DrawerKV label="Project" mono>
            <span
              style={{
                cursor: onOpenProject ? 'pointer' : 'default',
                color: onOpenProject ? 'var(--accent-text)' : 'var(--text-1)',
              }}
              onClick={() => onOpenProject && onOpenProject(job.project_id)}
            >{job.project_id}</span>
          </DrawerKV>
        )}
        <DrawerKV label="Requested at">{fmtTimestamp(job.requested_at)}</DrawerKV>
        {job.started_at && (
          <DrawerKV label="Started at">{fmtTimestamp(job.started_at)}</DrawerKV>
        )}
        {job.completed_at && (
          <DrawerKV label="Completed at">{fmtTimestamp(job.completed_at)}</DrawerKV>
        )}
        {job.attempts > 0 && (
          <DrawerKV label="Attempts" mono>{job.attempts}</DrawerKV>
        )}
        {job.error_code && (
          <DrawerKV label="Error code" mono valueColor="var(--danger)">{job.error_code}</DrawerKV>
        )}
        {job.dropbox_sign_status && (
          <DrawerKV label="Signature">
            <SignatureBadge
              status={job.dropbox_sign_status}
              testMode={job.dropbox_sign_test_mode}
            />
          </DrawerKV>
        )}
        {job.dropbox_sign_envelope_id && (
          <DrawerKV label="Envelope" mono>
            <span title={job.dropbox_sign_envelope_id}>{job.dropbox_sign_envelope_id}</span>
          </DrawerKV>
        )}
        {job.dropbox_sign_sent_at && (
          <DrawerKV label="Sent for signature">{fmtTimestamp(job.dropbox_sign_sent_at)}</DrawerKV>
        )}
        {job.dropbox_sign_completed_at && (
          <DrawerKV label="Signature completed">{fmtTimestamp(job.dropbox_sign_completed_at)}</DrawerKV>
        )}
      </div>
      {showFullMessage && (
        <div style={{
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          color: status === 'failed' ? 'var(--danger)' : 'var(--text-1)',
          fontSize: 13.5, lineHeight: 1.55,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          boxShadow: status === 'failed' ? 'inset 3px 0 0 var(--danger)' : 'none',
        }}>{showFullMessage}</div>
      )}
      {job.replay_of_job_id && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-3)' }}>
          ↳ This job is a replay of{' '}
          <span className="mono" style={{ color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
            {job.replay_of_job_id}
          </span>
        </div>
      )}
      {job.replays && job.replays.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-3)' }}>
          Replayed {job.replays.length} time{job.replays.length === 1 ? '' : 's'}:{' '}
          {job.replays.map((r, i) => (
            <span key={r.job_id}>
              <span className="mono" style={{ color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
                {r.job_id.slice(0, 12)}…
              </span>
              {' ('}{cleanStatus(r.status)}{')'}
              {i < job.replays.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const DrawerKV = ({ label, mono = false, valueColor, children }) => (
  <>
    <span style={{
      fontSize: 12, fontWeight: 600,
      color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      whiteSpace: 'nowrap', paddingTop: 2,
    }}>{label}</span>
    <span
      className={mono ? 'mono' : undefined}
      style={{
        color: valueColor || 'var(--text-1)',
        letterSpacing: mono ? '-0.02em' : '-0.005em',
      }}
    >{children}</span>
  </>
);

export default CEActivityPage;
